import React, {useEffect, useRef} from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import StyledMasonryLayout from './styles';
import Props, {ColumnsInterface} from './typings';
import useMounted from '../../../../hooks/use-mounted/use-mounted';

let gap = 0;
let items: any = [];
let columns = 0;
const defaultColumnLayout: ColumnsInterface = {
  xs: 2,
  s: 3,
  m: 3,
  l: 4,
};

const MasonryLayout = ({layout, skeleton: Skeleton, loading, children}: Props) => {
  layout = {columns: {...defaultColumnLayout, ...layout?.columns}};
  const positionedEl = useRef(0);
  const masonrySupported = useRef(false);
  const container = useRef<HTMLElement>(null);
  const prevSize = useRef({
    width: 0,
    height: 0,
  });
  const hasMounted = useMounted();
  const skeletons = useRef<JSX.Element[]>([]);

  // Reposition elements once more children are loaded
  useEffect(() => {
    if (!hasMounted || masonrySupported.current) return;
    if (React.Children.count(children) === 0) positionedEl.current = 0;
    setLayout();
  }, [React.Children.count(children)]);

  const setPositions = (staticElements) => {
    if (!container.current) return;

    const indexOffset = staticElements - columns;

    /* Do not touch already positioned elements */
    items.slice(staticElements).forEach((el, i) => {
      const prevPos = items[i + indexOffset].getBoundingClientRect().bottom;
      const currPos = el.getBoundingClientRect().top;

      el.style.marginTop = `${prevPos + gap - currPos}px`;
    });

    positionedEl.current = React.Children.count(children);
  };

  const getColumns = () => {
    if (container.current)
      columns = getComputedStyle(container.current).gridTemplateColumns.split(' ').length;
  };

  const setLayout = () => {
    if (!container.current) return;

    items = [...container.current.childNodes].filter(
      // eslint-disable-next-line max-len
      (el: HTMLElement) => el.nodeType === 1 && +getComputedStyle(el).gridColumnEnd !== -1,
    );
    getColumns();

    if (columns > 1) {
      if (items.length > columns)
        setPositions(positionedEl.current <= columns ? columns : positionedEl.current);
    }
  };

  const resizeListener = () => {
    if (!container.current) return;
    const newSize = container.current.getBoundingClientRect();
    if (prevSize.current.width === newSize.width) return;
    /* if reseting container - remove margins */
    items.forEach((el) => el.style.removeProperty('margin-top'));
    setLayout();
    prevSize.current = newSize;
  };

  useEffect(() => {
    if (loading) setLayout();
  }, [loading]);

  // const showSkeletons = () => {
  //   getColumns();
  //   let count = columns - (positionedEl.current % columns);
  //   if (count !== columns) count = +columns;
  //   console.log('>>> Add skeleton');
  //   return (
  //     <>
  //       {[...Array(count)].map((val, i) => (
  //         <Skeleton key={i} />
  //       ))}
  //     </>
  //   );
  // };

  useEffect(() => {
    let resizeObserver: any = null;

    if (Skeleton) {
      for (let i = 1; i <= layout.columns.l; i++) {
        skeletons.current.push(<Skeleton key={i} />);
      }
    }

    // if (getComputedStyle(container.current).gridTemplateRows !== 'masonry') {
    if (container.current) gap = parseFloat(getComputedStyle(container.current).rowGap);
    resizeObserver = new ResizeObserver((entries) => {
      // https://stackoverflow.com/a/58701523
      // We wrap it in requestAnimationFrame to avoid this error - Resize loop limit exceeded
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        // code to execute
        return resizeListener();
      });
    });
    resizeObserver.observe(container.current);
    // } else {
    //   masonrySupported.current = true;
    //   console.log('>>> Masonry is supported!');
    // }
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  return (
    <StyledMasonryLayout
      ref={container}
      $masonrySupported={masonrySupported.current}
      $columns={layout.columns}
    >
      {children}
      {skeletons.current.length > 0 && loading && skeletons.current}
    </StyledMasonryLayout>
  );
};

export default MasonryLayout;
